// projectRows — ONE drawing of a project row for its two hosts (ADR-080 §12 ·
// FRONTEND-PATTERNS §Navigation / shell): the switcher's menu and the project
// chooser page show the SAME rows — the brand dot · the name · one line
// under it ("<type> · <n> active sites") · a chip beside the name when blocked
// (Suspended · Expired) or on trial (the countdown) · the current one ticked —
// in ONE order (A–Z by the viewer's language, the current project first where
// there is one), with the family's search box past 8 (`SHORT_LIST`) matching
// by the shared rules. The hosts differ only in their container: a menu's rows
// (arrowed) against a page's links (tabbed) — never two drawings of a project.
// The list rule is the shared one (`appearsInProjectList`: non-terminated;
// blocked rows chipped and routed to the blocked page — FRONTEND-PATTERNS
// §Resolver chain); the count is the project's whole (not the viewer's reach).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { AvailableProject, MeResponse } from '@ems-app/schemas/auth/responses';
import { createMatcher } from '@ems-app/schemas/common/search-rules';
import { icons } from '../ui/icons.js';
import { useMe } from '../../hooks/useMe.js';
import type { OptionRow } from '../../hooks/useOptions.js';
import { appearsInProjectList, projectAccess, trialDaysLeft, type ProjectAccess } from '../../lib/projectAccess.js';

export interface ProjectRow {
  id: string;
  name: string;
  color: string;
  access: ProjectAccess;
  blocked: boolean;
  /** Where the row goes — the project, or the blocked page naming it. */
  to: string;
  /** The second line: the type · the active sites. */
  line: string;
  /** Beside the name: Suspended · Expired · "Trial · n days left"; null when there is nothing to say. */
  chip: { text: string; tone: 'blocked' | 'trial' } | null;
  current: boolean;
}

/** The viewer's project rows, in the one order. `t` carries the `nav` and `common` namespaces. */
export function projectRows(me: MeResponse, currentId: string | null, now: number, t: TFunction, language: string): ProjectRow[] {
  const rows = me.availableProjects.filter(appearsInProjectList).map((p): ProjectRow => {
    const access = projectAccess(p, now);
    const blocked = access !== 'ok';
    const current = p.id === currentId;
    let chip: ProjectRow['chip'] = null;
    if (access === 'suspended') chip = { text: t('nav:projectStatus.suspended'), tone: 'blocked' };
    else if (access === 'trial-expired') chip = { text: t('nav:projectStatus.expired'), tone: 'blocked' };
    else if (p.is_trial && p.trial_expires_at !== null) chip = { text: `${t('common:selectProject.trial')} · ${t('nav:banner.trial.daysLeft', { count: trialDaysLeft(p.trial_expires_at, now) })}`, tone: 'trial' };
    return {
      id: p.id,
      name: p.name,
      color: p.color_primary,
      access,
      blocked,
      current,
      // The current row re-enters; a blocked row routes to the explanation page, carrying the project id so the page can name it.
      to: blocked && !current ? `/project-blocked?reason=${access}&project=${p.id}` : `/p/${p.id}/`,
      line: `${t(`common:projectType.${p.type}`)} · ${t('common:selectProject.activeSites', { count: p.active_site_count })}`,
      chip,
    };
  });
  const collator = new Intl.Collator(language);
  rows.sort((a, b) => Number(b.current) - Number(a.current) || collator.compare(a.name, b.name));
  return rows;
}

/** The rows a typed text matches, by the shared rules and in their rank (the whole list when nothing is typed). */
export function matchProjectRows(rows: readonly ProjectRow[], text: string): ProjectRow[] {
  return createMatcher(rows, (r) => ({ name: r.name })).match(text, { order: 'ranked' });
}

/** The viewer's projects as a picker's rows (ADR-080 §3 — the code kind: `/me` already holds them) — the SAME row in
 *  the same order (A–Z, no current one): the dot · the name · "<type> · <n> active sites", a blocked or trial project's
 *  chip beside it (read once). `only` keeps the projects a field may take (a token's: the active ones). */
export function useProjectOptionRows(only?: (p: AvailableProject) => boolean): OptionRow[] {
  const { t, i18n } = useTranslation(['nav', 'common']);
  const { data: me } = useMe();
  const [now] = useState(() => Date.now());
  return useMemo(() => {
    if (!me) return [];
    const keep = only ? new Set(me.availableProjects.filter(only).map((p) => p.id)) : null;
    return projectRows(me, null, now, t, i18n.language)
      .filter((row) => keep === null || keep.has(row.id))
      .map((row) => ({
        id: row.id,
        label: row.name,
        description: row.line,
        icon: <span className="block h-2.5 w-2.5 rounded-full ring-1 ring-[var(--color-border)]" style={{ backgroundColor: row.color }} />,
        trailing: row.chip ? <span className="text-xs font-medium text-[var(--color-text-secondary)]">{row.chip.text}</span> : undefined,
        spoken: row.chip?.text,
        pickable: true,
        group: null,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `only` is a predicate the host passes inline; the rows follow `me`
  }, [me, now, t, i18n.language]);
}

/** The row's face — the dot · the name over its line · the tick or the chip; the host wraps it in its own row element. */
export function ProjectRowFace({ row }: { row: ProjectRow }) {
  return (
    <>
      <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-[var(--color-border)]" style={{ backgroundColor: row.color }} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-[var(--color-text-primary)]">{row.name}</span>
        <span className="block truncate text-xs text-[var(--color-text-secondary)]">{row.line}</span>
      </span>
      {row.current ? (
        <icons.check aria-hidden size={16} className="shrink-0 text-[var(--color-primary-display)]" />
      ) : row.chip ? (
        <span className={`shrink-0 text-xs font-medium ${row.chip.tone === 'trial' ? 'text-[var(--color-primary-display)]' : 'text-[var(--color-text-secondary)]'}`}>{row.chip.text}</span>
      ) : null}
    </>
  );
}
